import {
  all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { get } from 'lodash';
import { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  createOrderByParams,
  isValidFilter,
  successfulAssociation,
  wildcardFilter,
} from '@/utils/resultsHelper';
import selectors from '@/selectors';
import actions from '../constants';
import CollectionDatasetsService from '../service';
import {
  removeCollectionDatasetsError,
  removeCollectionDatasetsSuccess,
  showCollectionDatasetsResults,
  showCollectionDatasetsError,
  fetchCollectionDatasetsAssignableSuccess,
  fetchCollectionDatasetsAssignableError,
  addCollectionDatasetSuccess,
  addCollectionDatasetError,
} from '../actions';

function fetchDatasets(
  code,
  filter,
  assignableFlag,
  privateFlag,
  internalFlag,
  publicFlag,
  page,
  pageSize,
  requestable,
  queryable,
  orderBy,
) {
  return CollectionDatasetsService.datasets(
    code,
    filter,
    assignableFlag,
    privateFlag,
    internalFlag,
    publicFlag,
    page,
    pageSize,
    requestable,
    queryable,
    orderBy,
  )
    .then((response) => ({ response }))
    .catch((error) => ({ error }));
}

export function* fetchCollectionDatasets(action) {
  const items = yield select(selectors.collectionDatasets);
  const item = findResult(items, action.category, action.code);

  const {
    filter,
    pageSize,
    currentPage,
    privateFlag,
    internalFlag,
    publicFlag,
    orderBy,
    requestableFlag,
    queryableFlag,
  } = item;
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePrivate = action.privateFlag || privateFlag;
  const includeInternal = action.internalFlag || internalFlag;
  const includePublic = action.publicFlag || publicFlag;
  const orderByParams = createOrderByParams(orderBy);

  const { response, error } = yield call(
    fetchDatasets,
    action.code,
    wildcardFilter(filter),
    false,
    includePrivate,
    includeInternal,
    includePublic,
    page,
    size,
    requestableFlag,
    queryableFlag,
    orderByParams,
  );

  if (error) {
    yield put(showCollectionDatasetsError(action.category, action.code, error));
  } else {
    yield put(showCollectionDatasetsResults(
      action.category,
      action.code,
      get(response.additional, 'collection.name', ''),
      get(response.additional, 'collection.permissions', ''),
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* removeCollectionDatasets(action) {
  const collections = yield select(selectors.collectionDatasets);
  const collection = findResult(collections, action.category, action.code);
  const results = yield all(action.items.map(
    (i) => callAdapterResponseError(i, CollectionDatasetsService.removeDataset(action, i.code)),
  ));
  const [success, errors] = results.reduce((result, element) => {
    result[successfulAssociation(element, 'Collection not found')
    || successfulAssociation(element, 'Dataset not found') ? 0 : 1].push(element);
    return result;
  }, [[], []]);
  if (errors.length > 0) {
    yield put(removeCollectionDatasetsError(action.code, collection.name, errors));
  }
  if (success.length > 0) {
    yield put(removeCollectionDatasetsSuccess(action.category, action.code, collection, success));
    yield call(fetchCollectionDatasets, { category: action.category, code: action.code });
  }
}

export function* addCollectionDatasets(action) {
  const item = yield select(selectors.datasetAssigningCollection);
  const items = yield select(selectors.collectionDatasets);
  const collection = findResult(items, item.category, item.code);
  const results = yield all(collection.datasetsToAdd.results.map(
    (i) => callAdapterResponseError(i, CollectionDatasetsService.addDataset(item, i.code)),
  ));
  const [success, errors] = results.reduce((result, element) => {
    result[successfulAssociation(element, 'Collection not found')
    || successfulAssociation(element, 'Dataset not found') ? 0 : 1].push(element);
    return result;
  }, [[], []]);

  if (errors.length > 0) {
    yield put(addCollectionDatasetError(action.code, item.name, errors));
  }
  if (success.length > 0) {
    yield put(addCollectionDatasetSuccess(item.code, collection, success));
    yield call(fetchCollectionDatasets, { category: item.category, code: item.code });
  }
}

export function* fetchCollectionDatasetsAssignable(action) {
  const items = yield select(selectors.collectionDatasets);
  const item = findResult(items, action.category, action.code);

  const filter = action.filter || item.unassignedDatasets.filter;
  if (!isValidFilter(filter, item.unassignedDatasets)) { return; }

  const { response, error } = yield call(fetchDatasets, action.code, wildcardFilter(filter), true);
  if (error) {
    yield put(fetchCollectionDatasetsAssignableError(action.category, action.code, error));
  } else {
    yield put(fetchCollectionDatasetsAssignableSuccess(
      action.category,
      action.code,
      response.items,
      response.paging.total,
    ));
  }
}

export default function* collectionDatasetsApi() {
  yield takeEvery(
    [
      actions.FETCH_COLLECTION_DATASETS,
      actions.UPDATE_COLLECTION_DATASETS_FLAGS,
      actions.TOGGLE_COLLECTION_DATASET_COLUMN_ORDER_BY,
      actions.TOGGLE_COLLECTION_DATASET_SINGLE_SORTING,
    ],
    fetchCollectionDatasets,
  );
  yield takeEvery(actions.REMOVE_COLLECTION_DATASETS, removeCollectionDatasets);
  yield takeEvery(actions.FETCH_COLLECTION_DATASETS_ASSIGNABLE, fetchCollectionDatasetsAssignable);
  yield takeLatest(actions.ADD_COLLECTION_DATASETS, addCollectionDatasets);
}
